import Component from '@glimmer/component'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import {
  serialize as serializeSort,
  deserialize as deserializeSort,
  updateSortColumns,
} from '@blakeelearning/data-tables/utils/sorting'
import { action } from '@ember/object'
import type { SortingConfig } from '@blakeelearning/data-tables/utils/sorting'
import { getSortViewModel } from 'teacher-ui-client/utils/sorting'
import { t } from 'ember-intl'
import { hash } from '@ember/helper'
import { on } from '@ember/modifier'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import formatValue from 'teacher-ui-client/helpers/format-value'
import type { SpellingRuleData } from 'teacher-ui-client/components/writing-legends/spelling-rule'
import { LinkTo } from '@ember/routing'

interface Signature {
  Element: HTMLTableElement
  Args: {
    isReloading?: boolean
    spellingRuleData?: SpellingRuleData
  }
}

export class SpellingRuleTable extends Component<Signature> {
  @queryParam('reporting/ui-scope/writing-legends/spelling') rule!: string

  <template>
    <table class="divide-dusty-black-200 relative table-fixed divide-y" ...attributes>
      <thead>
        <tr class="bg-white">
          <ThSort
            @sortKey="spelling_issue"
            @text={{t "components.reporting.tables.writingLegends.spelling.spellingRule"}}
          />
          <ThSort
            class="w-72"
            @sortKey="total_written"
            @text={{t "components.reporting.tables.writingLegends.spelling.totalWordsWritten"}}
          />
          <ThSort
            @sortKey="total_incorrect"
            class="w-56"
            @text={{t "components.reporting.tables.writingLegends.spelling.totalIncorrect"}}
          />
          <ThSort
            class="w-36"
            @sortKey="error_rate"
            @text={{t "components.reporting.tables.writingLegends.spelling.errorRate"}}
          />
        </tr>
      </thead>
      <tbody class="divide-dusty-black-200 divide-y {{if @isReloading 'opacity-50'}}">
        {{#each @spellingRuleData.issues as |issue index|}}
          <tr class="bg-white" data-test-table-row="{{index}}">
            <td class="truncate p-3" data-test-cell="spelling_issue">
              <LinkTo @query={{hash rule=issue.spelling_issue_id}}>{{issue.spelling_issue}}</LinkTo>
            </td>
            <td class="truncate p-3" data-test-cell="total_written">
              {{issue.total_written}}
            </td>
            <td class="p-3" data-test-cell="total_incorrect">
              {{issue.total_incorrect}}
            </td>
            <td class="p-3" data-test-cell="error_rate">
              {{formatValue issue.error_rate append="%"}}
            </td>
          </tr>
        {{/each}}
      </tbody>
    </table>
  </template>
}

interface ThSortSignature {
  Element: HTMLTableCellElement
  Args: {
    text?: string
    sortKey: string
  }
}

class ThSort extends Component<ThSortSignature> {
  @queryParam('reporting/ui-scope/writing-legends/spelling') sort!: string

  get sortingConfig() {
    return deserializeSort(this.sort)
  }

  set sortingConfig(newSortConfig: SortingConfig) {
    this.sort = serializeSort(newSortConfig)
  }

  @action
  updateSort() {
    this.sortingConfig = updateSortColumns(this.sortingConfig, this.args.sortKey)
  }

  get sortViewModel() {
    return getSortViewModel(this.sortingConfig, this.args.sortKey)
  }

  <template>
    <th
      class="h-[80px] select-none"
      data-test-sort-key={{@sortKey}}
      aria-sort={{this.sortViewModel.ariaDirection}}
      ...attributes
    >
      <button
        type="button"
        class="flex h-full w-full items-center gap-x-3 p-3 text-left {{if @text 'justify-between' 'justify-end'}}"
        {{on "click" this.updateSort}}
      >
        {{#if @text}}<span class="text-dusty-black-250 font-normal uppercase">{{@text}}</span>{{/if}}
        <PhosphorIcon @icon={{this.sortViewModel.icon}} class="text-dusty-black-400 print:hidden" />
      </button>
    </th>
  </template>
}
